/* eslint-disable complexity */
import { type GridOption } from 'echarts/types/dist/shared';
import ExtensionAPI from 'echarts/lib/core/ExtensionAPI';
import GlobalModel from 'echarts/lib/model/Global';
import { getInstanceById } from 'echarts/core';
import * as graphic from 'echarts/lib/util/graphic';
import ComponentView from 'echarts/lib/view/Component';
import RoamController from 'echarts/lib/component/helper/RoamController';
import { Payload } from 'echarts/lib/util/types';
import { type ElementAnimateConfig } from 'zrender/lib/Element.js';
import { invert } from 'zrender/lib/core/matrix.js';
import GridModel from 'echarts/lib/coord/cartesian/GridModel';
// @ts-ignore
import { parsePercent } from 'echarts/lib/util/number';
import { type ZRenderType } from 'zrender/lib/zrender.js';
import { findMost } from '../../../util/array';
import { type AxesControllerHost, doUpdateAxesViewOnPanAndZoom } from './axesRoamHelper';
import RoamModel, { type RoamOption } from './RoamModel';
import { type ControllerHost, doUpdateSeriesViewOnPanAndZoom } from './roamHelper';
import { type EventDefinition } from './types';

type Point = [number, number];

// 记录临时变量
let resetFns: any[] = [];

class RoamView extends ComponentView {
  static type = 'roam';

  type = RoamView.type;

  /** 被影响的部分 */
  static readonly seriesType = ['series'];

  static readonly axesType = ['xAxis', 'yAxis'];

  roamModel!: RoamModel;

  ecModel!: GlobalModel;

  api!: ExtensionAPI;

  private __axesGroupArr!: graphic.Group[];

  private __seriesGroupArr!: graphic.Group[];

  private __overflowTimer: ReturnType<typeof setTimeout> | null = null;

  private __overflowPosArr: string[] = [];

  protected __controller!: RoamController;

  // 存在重复属性，但是分发更加符合ECharts工具设计，暂不处理
  /**
   * 非重复结构：
   * {
   *  target: Element[],
   *  zoom: number,
   *  zoomLimit: number,
   * }
   */
  private __seriesControllerHosts!: ControllerHost[];

  // 轴线需要特殊处理
  private __axesControllerHosts!: AxesControllerHost[];

  // 默认视图中心
  private __defaultCenter!: Point;

  // 默认视图缩放
  private __defaultZoom!: number;

  private __initRendered: boolean;

  private __panDirection: null | Exclude<RoamOption['panDirection'], 'x|y'>;

  init(ecModel: GlobalModel, api: ExtensionAPI) {
    this.ecModel = ecModel;
    this.api = api;
    this.__controller = new RoamController(api.getZr());
    this.__seriesControllerHosts = [];
  }

  render(roamModel: RoamModel, ecModel: GlobalModel, api: ExtensionAPI, payload: Payload): void {
    this.roamModel = roamModel;
    this.__initCenterAndZoom(payload);

    if (!this.__initRendered) {
      this.__initRendered = true;

      /**
       * 初始化事件对象
       * 必须放在render中，因为在init中无法获取到group
       */
      this.__seriesGroupArr = this.__getSeriesGroupArr();
      this.__axesGroupArr = this.__getAxesGroupArr();
      this.__seriesControllerHosts = this.__seriesGroupArr.map(
        d =>
          ({
            target: d,
            // 目前X与Y不支持独立缩放，所以将X的缩放视为全局缩放倍数
            zoom: d.transform ? d.transform[0] : 1,
            zoomLimit: roamModel.get('scaleLimit')!
          } as unknown as ControllerHost)
      );
      this.__axesControllerHosts = this.__axesGroupArr.map(
        d =>
          ({
            target: d,
            zoom: d.transform ? d.transform[0] : 1,
            zoomLimit: roamModel.get('scaleLimit')!,
            axes: (d as any).__ecComponentInfo?.mainType
          } as unknown as ControllerHost)
      ) as AxesControllerHost[];
    }

    /**
     * 绑定roam事件
     */
    this.__controllerHandler(roamModel);
  }

  private __initCenterAndZoom(payload) {
    const { type } = payload || {};

    // 仅默认情况和resize时更新center和zoom
    if (type && type !== 'resize') {
      return;
    }
    const { zoom = 1, center = ['50%', '50%'] } = RoamModel.defaultOption;
    this.__defaultZoom = zoom;
    const centerFormatted: Point = [
      parsePercent(center[0], this.api.getWidth()),
      parsePercent(center[1], this.api.getHeight())
    ];
    this.__defaultCenter = centerFormatted;
  }

  private __getSeriesGroupArr() {
    const zrIns = this.api.getZr();
    const { storage } = zrIns;
    const seriesGroupArr: graphic.Group[] = [];
    storage.getRoots().forEach(root => {
      const mainType = (root as any).__ecComponentInfo?.mainType ?? null;
      if (RoamView.seriesType.includes(mainType)) {
        seriesGroupArr.push(root as graphic.Group);
      }
    });
    return seriesGroupArr;
  }

  private __getAxesGroupArr() {
    const zrIns = this.api.getZr();
    const { storage } = zrIns;
    const axesGroupArr: graphic.Group[] = [];
    storage.getRoots().forEach(root => {
      const mainType = (root as any).__ecComponentInfo?.mainType ?? null;
      if (RoamView.axesType.includes(mainType)) {
        axesGroupArr.push(root as graphic.Group);
      }
    });
    return axesGroupArr;
  }

  private __handleMouseUp() {
    if (this.roamModel.get('panDirection') === 'x|y') {
      this.__panDirection = null;
    }
  }

  private __controllerHandler(roamModel: RoamModel) {
    const { api } = this;
    const ecIns = getInstanceById(api.getId());
    const controller = this.__controller;
    const self = this;

    controller.enable(roamModel.get('roam'), {
      api,
      zInfo: { component: roamModel },
      triggerInfo: {
        roamTrigger: roamModel.get('roamTrigger'),
        isInSelf() {
          return true;
        }
      }
    });
    if (self.roamModel.get('panDirection') === 'x|y') {
      self.__panDirection = null;
    } else {
      self.__panDirection = self.roamModel.get('panDirection') as Exclude<
        RoamOption['panDirection'],
        'x|y'
      >;
    }

    const zrIns = controller._zr as ZRenderType;
    zrIns.off('mouseup', () => this.__handleMouseUp());
    zrIns.on('mouseup', () => this.__handleMouseUp(), self);

    // 绑定监听
    controller
      .off('pan')
      .off('zoom')
      .on('pan', e => {
        ecIns.trigger('dvbeforeroam', { type: 'pan', event: e });
        // 单向平移
        if (!self.__panDirection) {
          const [dx, dy] = [Math.abs(e.dx), Math.abs(e.dy)];
          if (dx === dy) {
            return;
          }

          if (dx > dy) {
            self.__panDirection = 'x';
          } else {
            self.__panDirection = 'y';
          }
        }

        const panRatio = roamModel.get('panRatio');
        if (this.__panDirection === 'x') {
          self.__panHandler(e.dx * panRatio, 0);
        } else if (this.__panDirection === 'y') {
          self.__panHandler(0, e.dy * panRatio);
        } else {
          self.__panHandler(e.dx * panRatio, e.dy * panRatio);
        }
        ecIns.trigger('dvafterroam', { type: 'pan', event: e });
      })
      .on('zoom', (e: any) => {
        ecIns.trigger('dvbeforeroam', { type: 'zoom', event: e });
        const oldScale = e.scale as number;
        const zoomRatio = roamModel.get('zoomRatio');
        const newScale = 1 + (oldScale - 1) * zoomRatio;
        self.__zoomHandler(newScale, e.originX, e.originY);
        ecIns.trigger('dvafterroam', { type: 'zoom', event: e });
      });
  }

  private __panHandler(dx: number, dy: number, needLimit = true, triggerEvent = true) {
    this.updateAllViewOnPanAndZoom({ dx, dy }, false, triggerEvent, needLimit);
    this.__clipHandler();
  }

  private __zoomHandler(
    zoomDelta: number,
    zoomX: number,
    zoomY: number,
    needLimit = true,
    triggerEvent = true
  ) {
    const { api } = this;
    this.updateAllViewOnPanAndZoom({ zoomDelta, zoomX, zoomY }, false, triggerEvent, needLimit);
    this.__clipHandler();
    // Only update label layout on zoom
    api.updateLabelLayout();
    api.getZr().refresh();
  }

  // 获取所有series的外接矩形
  private __getContentRect() {
    const { api } = this;
    const seriesGroupArr = this.__getSeriesGroupArr();
    let globalRect: graphic.BoundingRect;
    seriesGroupArr.forEach((group, i) => {
      // shake empty tree
      group.traverse(el => {
        // 如果存在内容为空的group，应该删除
        // 因为空group本身存在位置信息，会导致最终的rect计算错误
        // 若存在异步操作，导致此处group判空，则手动添加自定义属性isAsync标明group为异步添加子元素
        if (
          el.type === 'group' &&
          (el as graphic.Group).children().length === 0 &&
          !(el as graphic.Group).isAsync &&
          el.parent
        ) {
          el.parent.remove(el);
        }
      });
      const rect = group.getBoundingRect().clone();

      const model = api.getModel().getSeriesByIndex(i);
      const coordSys = model.coordinateSystem;
      if (coordSys) {
        const coordRect = coordSys.getBoundingRect().clone();
        rect.union(coordRect);
      }
      const transform = group.getComputedTransform();
      if (transform) {
        rect.applyTransform(group.transform);
      }
      if (i === 0) {
        globalRect = rect;
      } else {
        globalRect.union(rect);
      }
    });
    return globalRect;
  }

  // 视窗中grid的大小
  private __getWindowRect() {
    const zrIns = this.api.getZr();
    const zrWidth = zrIns.getWidth();
    const zrHeight = zrIns.getHeight();
    const gridModel = this.api.getModel().getComponent('grid') as GridModel;
    const coordSys = gridModel.coordinateSystem;
    if (!coordSys.getAxes().length) {
      return new graphic.BoundingRect(0, 0, zrWidth, zrHeight);
    }
    const gridOption = gridModel.option;
    const { left, right, top, bottom } = gridOption as GridOption;
    const leftFormat = parsePercent(left, zrWidth);
    const rightFormat = parsePercent(right, zrWidth);
    const topFormat = parsePercent(top, zrHeight);
    const bottomFormat = parsePercent(bottom, zrHeight);

    return new graphic.BoundingRect(
      leftFormat,
      topFormat,
      zrWidth - leftFormat - rightFormat,
      zrHeight - topFormat - bottomFormat
    );
  }

  private __roamLimitHandler() {
    const { api } = this;
    const ecIns = getInstanceById(api.getId());
    // content
    const contentRect = this.__getContentRect();
    // window
    const windowRect = this.__getWindowRect();

    // 判断X方向与Y方向上俩者的大小关系
    const isContentWidthBigger = contentRect.width > windowRect.width;
    const isContentHeightBigger = contentRect.height > windowRect.height;

    // overflow：正数表示超出，负数表示未超出
    const overflowLeft = isContentWidthBigger
      ? contentRect.x - windowRect.x
      : windowRect.x - contentRect.x;
    const overflowTop = isContentHeightBigger
      ? contentRect.y - windowRect.y
      : windowRect.y - contentRect.y;
    const overflowRight = isContentWidthBigger
      ? windowRect.x + windowRect.width - (contentRect.x + contentRect.width)
      : contentRect.x + contentRect.width - (windowRect.x + windowRect.width);
    const overflowBottom = isContentHeightBigger
      ? windowRect.y + windowRect.height - (contentRect.y + contentRect.height)
      : contentRect.y + contentRect.height - (windowRect.y + windowRect.height);

    let fixOffsetX = 0;
    let fixOffsetY = 0;

    let overflowPos: string | null = null;
    // top、left优先级高于bottom、right
    if (overflowRight > 0) {
      // 避免偏移超出另一侧
      fixOffsetX = Math.min(overflowRight, Math.abs(overflowLeft));
      overflowPos = 'right';
    }
    if (overflowLeft > 0) {
      fixOffsetX = -Math.min(overflowLeft, Math.abs(overflowRight));
      overflowPos = 'left';
    }

    if (overflowBottom > 0) {
      fixOffsetY = Math.min(overflowBottom, Math.abs(overflowTop));
      overflowPos = 'bottom';
    }
    if (overflowTop > 0) {
      fixOffsetY = -Math.min(overflowTop, Math.abs(overflowBottom));
      overflowPos = 'top';
    }

    // 计算修正偏移量
    fixOffsetX = isContentWidthBigger ? fixOffsetX : -fixOffsetX;
    fixOffsetY = isContentHeightBigger ? fixOffsetY : -fixOffsetY;

    // 触发事件
    // FIXME: 此处需要更优雅健壮的函数
    if (overflowPos) {
      ecIns.trigger('dvroamoverflow', overflowPos);

      this.__overflowPosArr.push(overflowPos);
      clearTimeout(this.__overflowTimer);
      this.__overflowTimer = setTimeout(() => {
        ecIns.trigger('dvroamoverflowend', findMost(this.__overflowPosArr));
        this.__overflowPosArr = [];
      }, 300);
    }

    return { fixOffsetX, fixOffsetY };
  }

  private __clipHandler() {
    // clip only apply to series when has axes
    if (!this.__axesGroupArr.length) {
      return;
    }

    // get transform
    const seriesGroupArr = this.__seriesGroupArr;
    const DEFAULT_TRANSFORM = [1, 0, 0, 1, 0, 0];
    // avoid clipping graph that near the limit
    const fixGap = 1;

    // series Clip
    // get clip rect path
    const seriesTransform = seriesGroupArr[0].getComputedTransform() || DEFAULT_TRANSFORM;
    const invertSeriesTransform = invert([], seriesTransform);
    const seriesWindowRect = this.__getWindowRect().clone();

    const gridClipPath = () =>
      new graphic.Rect({
        shape: {
          x: seriesWindowRect.x,
          y: seriesWindowRect.y,
          width: seriesWindowRect.width + fixGap,
          height: seriesWindowRect.height + fixGap
        }
      });

    const gridWindowClipPath = gridClipPath();

    gridWindowClipPath.setLocalTransform(invertSeriesTransform);
    gridWindowClipPath.updateTransform();

    seriesGroupArr.forEach(group => {
      group.removeClipPath();
      group.setClipPath(gridWindowClipPath);
    });

    // axes Clip
    const xClipPath = () =>
      new graphic.Rect({
        shape: {
          x: seriesWindowRect.x,
          y: 0,
          width: seriesWindowRect.width,
          height: this.api.getHeight()
        }
      });

    const yClipPath = () =>
      new graphic.Rect({
        shape: {
          x: 0,
          y: seriesWindowRect.y,
          width: this.api.getWidth(),
          height: seriesWindowRect.height
        }
      });

    const axesGroupArr = this.__axesGroupArr;

    axesGroupArr.forEach(group => {
      // @ts-ignore
      const { __ecComponentInfo = {} } = group;
      const { mainType } = __ecComponentInfo || {};
      if (!['xAxis', 'yAxis'].includes(mainType)) {
        return;
      }

      group.traverse(d => {
        if (!d.anid) {
          return;
        }
        const clipPathTemp = mainType === 'xAxis' ? xClipPath() : yClipPath();
        const gridPathTemp = gridClipPath();
        const axesItemTransform = d.getComputedTransform() || DEFAULT_TRANSFORM;
        const invertAxesItemTransform = invert([], axesItemTransform);
        clipPathTemp.setLocalTransform(invertAxesItemTransform);
        gridPathTemp.setLocalTransform(invertAxesItemTransform);
        clipPathTemp.updateTransform();
        gridPathTemp.updateTransform();

        d.removeClipPath();
        // grid外：tick、label
        if (d.anid.includes('tick') || d.anid.includes('label')) {
          d.setClipPath(clipPathTemp);
        }
        // grid内：axisLine、splitLine
        if (d.anid.includes('line')) {
          d.setClipPath(gridPathTemp);
        }
      });
    });
  }

  getController() {
    return this.__controller;
  }

  getCenterAndZoom() {
    /**
     * 从第一个seriesGroup的变换矩阵上获取偏移和缩放
     */
    const firstSeries = this.__seriesGroupArr[0];
    const transform = firstSeries.getComputedTransform();
    if (!transform) {
      return {
        center: this.__defaultCenter,
        zoom: this.__defaultZoom
      };
    }
    /**
     * transform[0]:scaleX
     * transform[1]:skewY
     * transform[2]:skewX
     * transform[3]:scaleY
     * transform[4]:translateX
     * transform[5]:translateY
     *
     * 此场景下scaleX总是等于scaleY
     */
    const zoom = transform[0] / 1;
    const center = [
      (this.__defaultCenter[0] - transform[4]) / zoom,
      (this.__defaultCenter[1] - transform[5]) / zoom
    ];
    return {
      center,
      zoom
    };
  }

  getAllViewState() {
    const states = {} as Parameters<EventDefinition['dvroam']>[0]['seriesStates'];

    this.__seriesControllerHosts.forEach((_, index) => {
      states[index] = this.getSeriesViewState(index);
    });

    return states;
  }

  getSeriesViewState(seriesIndex: number) {
    const controllerHost = this.__seriesControllerHosts[seriesIndex];

    return { zoom: controllerHost?.zoom, zoomLimit: controllerHost?.zoomLimit };
  }

  /**
   * ! 该方法只会更新状态，但不会刷新视图
   */
  setSeriesViewState(
    seriesIndex: number,
    { zoom, zoomLimit }: Pick<ControllerHost, 'zoom' | 'zoomLimit'>
  ) {
    const controllerHost = this.__seriesControllerHosts[seriesIndex];

    controllerHost.zoom = zoom ?? controllerHost.zoom;
    controllerHost.zoomLimit = zoomLimit ?? controllerHost.zoomLimit;
  }

  updateAllViewOnPanAndZoom(
    {
      dx = 0,
      dy = 0,
      zoomDelta = 1,
      zoomX = this.api.getWidth() / 2,
      zoomY = this.api.getHeight() / 2,
      dirty = true
    }: {
      dx?: number;
      dy?: number;
      zoomDelta?: number;
      zoomX?: number;
      zoomY?: number;
      dirty?: boolean;
    },
    animation?: boolean | ElementAnimateConfig,
    triggerEvent = true,
    needLimit = true
  ) {
    /**
     * 这里的代码逻辑较为复杂，描述如下：
     * 1.如果needLimit为false，则正常执行，触发transform更新，并触发视图更新（dirty控制）
     * 2.如果needLimit为true，则只触发transform跟新，不触发视图更新
     *  2.1 当transform更新后，执行__roamLimitHandler的计算逻辑，因为该逻辑依赖更新后的transform用于计算外包围盒
     *  2.2 得到需要修正的偏移量fixOffsetX, fixOffsetY
     *  2.3 还原transform（还原是为了最后只渲染一次，从而让动画能够正常执行）
     *  2.4 用修正后的偏移量重新执行本段逻辑
     *
     *  本来的旧逻辑：先位移，更新transform，不刷新视图；再计算偏移量，再更新transform（仅偏移），再刷新视图(此时如果用动画，动画只会处理偏移的部分)
     *  为了适配动画：先位移，更新transform，不刷新视图；再计算偏移量，还原transform（新增该处理），再更新transform（偏移+位移），再刷新视图
     */
    const self = this;
    resetFns = [];

    self.__axesControllerHosts.forEach(d => {
      self.updateAxesViewOnPanAndZoom(
        d,
        { dx, dy, zoomDelta, zoomX, zoomY, dirty: dirty && !needLimit },
        animation,
        false
      );
    });

    self.__seriesControllerHosts.forEach((_, index) => {
      self.updateSeriesViewOnPanAndZoom(
        index,
        { dx, dy, zoomDelta, zoomX, zoomY, dirty: dirty && !needLimit },
        animation,
        false,
        false
      );
    });

    if (needLimit) {
      // 计算偏移量
      const { fixOffsetX, fixOffsetY } = self.__roamLimitHandler();
      // 还原transform
      resetFns.forEach(d => d());
      // 重新执行本段逻辑
      self.updateAllViewOnPanAndZoom(
        {
          dx: dx + fixOffsetX,
          dy: dy + fixOffsetY,
          zoomDelta,
          zoomX,
          zoomY
        },
        animation,
        true,
        false
      );
    }

    if (triggerEvent && !needLimit) {
      const ecIns = getInstanceById(self.api.getId());
      ecIns.trigger('dvroam', {
        type: 'dvroam',
        dx,
        dy,
        zoomDelta,
        zoomX,
        zoomY,
        seriesStates: self.getAllViewState()
      } as Parameters<EventDefinition['dvroam']>[0]);
    }
  }

  updateSeriesViewOnPanAndZoom(
    seriesIndex: number,
    {
      dx = 0,
      dy = 0,
      zoomDelta = 1,
      zoomX = this.api.getWidth() / 2,
      zoomY = this.api.getHeight() / 2,
      dirty = true
    }: {
      dx?: number;
      dy?: number;
      zoomDelta?: number;
      zoomX?: number;
      zoomY?: number;
      dirty?: boolean;
    },
    animation?: boolean | ElementAnimateConfig,
    triggerEvent = true,
    roamLimit = true
  ) {
    if (roamLimit) {
      resetFns = [];
    }
    const controllerHost = this.__seriesControllerHosts[seriesIndex];
    this.handleUpdateViewOnPanAndZoom(
      controllerHost,
      { dx, dy, zoomDelta, zoomX, zoomY, dirty: dirty && !roamLimit },
      doUpdateSeriesViewOnPanAndZoom,
      animation,
      triggerEvent && !roamLimit
    );

    if (roamLimit) {
      // 计算偏移量
      const { fixOffsetX, fixOffsetY } = this.__roamLimitHandler();
      // 还原transform
      resetFns.forEach(d => d());
      // 重新执行本段逻辑
      this.updateSeriesViewOnPanAndZoom(
        seriesIndex,
        {
          dx: dx + fixOffsetX,
          dy: dy + fixOffsetY,
          zoomDelta,
          zoomX,
          zoomY
        },
        animation,
        true,
        false
      );
    }
  }

  updateAxesViewOnPanAndZoom(
    controllerHost: AxesControllerHost,
    {
      dx,
      dy,
      zoomDelta,
      zoomX = this.api.getWidth() / 2,
      zoomY = this.api.getHeight() / 2,
      dirty = true
    }: {
      dx?: number;
      dy?: number;
      zoomDelta?: number;
      zoomX?: number;
      zoomY?: number;
      dirty?: boolean;
    },
    animation?: boolean | ElementAnimateConfig,
    triggerEvent = true
  ) {
    this.handleUpdateViewOnPanAndZoom(
      controllerHost,
      { dx, dy, zoomDelta, zoomX, zoomY, dirty },
      doUpdateAxesViewOnPanAndZoom,
      animation,
      triggerEvent
    );
  }

  handleUpdateViewOnPanAndZoom(
    controllerHost: ControllerHost,
    {
      dx,
      dy,
      zoomDelta,
      zoomX = this.api.getWidth() / 2,
      zoomY = this.api.getHeight() / 2,
      dirty = true
    }: {
      dx?: number;
      dy?: number;
      zoomDelta?: number;
      zoomX?: number;
      zoomY?: number;
      dirty?: boolean;
    },
    updateFn: any,
    animation?: boolean | ElementAnimateConfig,
    triggerEvent = true
  ) {
    const reset = updateFn(controllerHost, { dx, dy, zoomDelta, zoomX, zoomY, dirty }, animation);
    resetFns.push(reset);
    if (triggerEvent) {
      const ecIns = getInstanceById(this.api.getId());
      ecIns.trigger('dvroam', {
        type: 'dvroam',
        dx,
        dy,
        zoomDelta,
        zoomX,
        zoomY,
        seriesStates: this.getAllViewState()
      } as Parameters<EventDefinition['dvroam']>[0]);
    }
  }

  setCenterAndZoom() {
    const { api } = this;
    const { roamModel } = this;
    // target center
    const newCenter = roamModel.get('center');
    // target scale
    const newZoom = roamModel.get('zoom');
    const newCenterParsed: Point = [
      parsePercent(newCenter[0], api.getWidth()),
      parsePercent(newCenter[1], api.getHeight())
    ];

    const newCenterFormatted = [
      newCenterParsed[0] + (this.__defaultCenter[0] * (newZoom - 1)) / newZoom,
      newCenterParsed[1] + (this.__defaultCenter[1] * (newZoom - 1)) / newZoom
    ];

    let offset: Point = [0, 0];

    offset = [
      this.__defaultCenter[0] - newCenterFormatted[0],
      this.__defaultCenter[1] - newCenterFormatted[1]
    ];
    /**
     * @see https://github.com/apache/echarts/blob/master/src/action/roamHelper.ts#L40
     * 这里的参数没有搞懂原理，因为没有指定正确的缩放中心，但是却正常生效
     *
     * triggerEvent: false，因为是API调用
     */
    this.__panHandler(offset[0], offset[1], true, false);
    this.__zoomHandler(newZoom / this.__defaultZoom, 0, 0, true, false);
  }
}
export default RoamView;
